'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { fetchTopPaddles, type Paddle } from '@/lib/services/paddleService';
import { useComparison } from '@/contexts/ComparisonContext';

export default function TopPaddlesPage() {
  const { addItem, removeItem, isSelected } = useComparison();
  const [topRankedPaddles, setTopRankedPaddles] = useState<Paddle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopPaddles().then(data => {
      setTopRankedPaddles(data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#EBEBEB' }}>
      <Navigation />

      {/* Header */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden" style={{ background: '#FFFFFF' }}>
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 75% -5%, rgba(217,0,18,0.04) 0%, transparent 55%)',
        }} />
        <div className="max-w-container mx-auto relative z-10">
          <div className="breadcrumb mb-6">
            <Link href="/" style={{ textDecoration: 'none' }}>Home</Link>
            <span className="breadcrumb-sep">/</span>
            <span style={{ color: 'rgba(10,10,10,0.55)' }}>Top Paddles</span>
          </div>
          <div className="premium-chip accent mb-6">Rankings</div>
          <h1
            className="font-black mb-4"
            style={{
              fontSize: 'clamp(32px, 5vw, 58px)',
              letterSpacing: '-0.048em',
              color: '#0A0A0A',
              lineHeight: 1.02,
            }}
          >
            Top Pickleball Paddles
          </h1>
          <p style={{ color: 'rgba(10,10,10,0.44)', fontSize: '16px', lineHeight: 1.75 }}>
            Ranked by performance data — paddles from 57+ brands
          </p>
        </div>
      </section>

      <div className="max-w-container mx-auto px-4">
        <div className="premium-divider" />
      </div>

      <main className="max-w-container mx-auto px-4 py-18" style={{ paddingTop: '4.5rem', paddingBottom: '4.5rem' }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div style={{ color: 'rgba(10,10,10,0.4)', fontSize: '14px' }}>Loading rankings…</div>
          </div>
        ) : (
          <>
            {/* Top 3 podium */}
            <section className="mb-18" style={{ marginBottom: '4.5rem' }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {topRankedPaddles?.slice(0, 3)?.map((paddle, i) => {
                  const selected = isSelected(paddle?.id);
                  const podiumStyles = [
                    {
                      badge: '#D90012',
                      badgeText: '#fff',
                      label: '#1 Best Overall',
                      border: 'rgba(217,0,18,0.22)',
                      topAccent: 'linear-gradient(90deg, #E8001A, #A80010)',
                      shadow: '0 8px 32px rgba(217,0,18,0.14), 0 2px 8px rgba(0,0,0,0.06)',
                    },
                    {
                      badge: '#5A5A5A',
                      badgeText: '#fff',
                      label: '#2 Runner Up',
                      border: 'rgba(90,90,90,0.16)',
                      topAccent: 'linear-gradient(90deg, #5A5A5A, #3A3A3A)',
                      shadow: 'var(--shadow-card)',
                    },
                    {
                      badge: '#374151',
                      badgeText: '#fff',
                      label: '#3 Third Place',
                      border: 'rgba(55,65,81,0.12)',
                      topAccent: 'linear-gradient(90deg, #374151, #1F2937)',
                      shadow: 'var(--shadow-card)',
                    },
                  ][i];
                  return (
                    <div
                      key={paddle?.id}
                      className="rounded-2xl overflow-hidden"
                      style={{
                        background: '#FFFFFF',
                        border: `1.5px solid ${podiumStyles.border}`,
                        boxShadow: podiumStyles.shadow,
                        transition: 'transform var(--transition), box-shadow var(--transition)',
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.transform = 'translateY(-6px)';
                        (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-card-hover)';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                        (e.currentTarget as HTMLElement).style.boxShadow = podiumStyles.shadow;
                      }}
                    >
                      {/* Top accent bar */}
                      <div style={{ height: 4, background: podiumStyles.topAccent }} />
                      <div
                        className="py-3 text-center font-black uppercase"
                        style={{
                          background: `${podiumStyles.badge}0F`,
                          color: podiumStyles.badge,
                          letterSpacing: '0.13em',
                          fontSize: '10px',
                          borderBottom: `1.5px solid ${podiumStyles.border}`,
                        }}
                      >
                        {podiumStyles.label}
                      </div>
                      <Link href={`/paddles/${paddle?.slug}`} className="block p-5" style={{ textDecoration: 'none' }}>
                        <div
                          className="w-full aspect-square rounded-2xl overflow-hidden mb-4"
                          style={{ background: 'linear-gradient(145deg, #F8F8F8 0%, #EFEFEF 100%)' }}
                        >
                          <img
                            src={paddle?.image}
                            alt={paddle?.imageAlt}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
                          />
                        </div>
                        <div
                          className="mb-1 font-bold uppercase"
                          style={{ color: 'rgba(13,13,13,0.34)', letterSpacing: '0.13em', fontSize: '9px' }}
                        >{paddle?.brand}</div>
                        <h3
                          className="text-base font-black mb-1.5"
                          style={{ letterSpacing: '-0.035em', color: '#0D0D0D', lineHeight: 1.2 }}
                        >{paddle?.name}</h3>
                        {paddle?.firepowerTier && (
                          <div className="text-xs font-semibold mb-3" style={{ color: '#D90012' }}>{paddle?.firepowerTier}</div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-xl font-black tabular-nums" style={{ letterSpacing: '-0.045em', color: '#0D0D0D' }}>{paddle?.priceFormatted}</span>
                          <span className="badge-red text-sm font-black px-3 py-1">{paddle?.score} pts</span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {paddle?.approvalBody && (
                            <span className="badge-neutral">{paddle?.approvalBody}</span>
                          )}
                          {paddle?.shape && (
                            <span className="badge-neutral">{paddle?.shape}</span>
                          )}
                        </div>
                      </Link>
                      <div className="px-5 pb-5">
                        <button
                          onClick={() => selected ? removeItem(paddle?.id) : addItem(paddle)}
                          className={`add-compare-btn${selected ? ' selected' : ''}`}
                        >
                          {selected ? '✓ Added to Compare' : '+ Add to Compare'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Full ranked list */}
            <section>
              <h2 className="section-heading mb-6">Full Rankings</h2>
              <div
                className="rounded-2xl overflow-hidden"
                style={{
                  background: '#FFFFFF',
                  border: '1px solid rgba(0,0,0,0.055)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.03)',
                }}
              >
                {/* Header */}
                <div
                  className="grid grid-cols-12 px-5 py-4"
                  style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', background: '#F5F5F5' }}
                >
                  {['#', 'Paddle', 'Score', 'Price', 'Firepower', '+'].map((col, ci) => (
                    <div
                      key={col}
                      className={`text-xs font-bold uppercase ${ci === 0 ? 'col-span-1' : ci === 1 ? 'col-span-4' : ci === 5 ? 'col-span-1 text-center' : 'col-span-2 text-center'}`}
                      style={{ color: 'rgba(13,13,13,0.34)', letterSpacing: '0.11em' }}
                    >{col}</div>
                  ))}
                </div>
                {topRankedPaddles?.map((paddle, i) => {
                  const selected = isSelected(paddle?.id);
                  const rankStyles = [
                    { bg: 'rgba(217,0,18,0.07)', color: '#D90012', border: 'rgba(217,0,18,0.16)' },
                    { bg: 'rgba(90,90,90,0.07)', color: '#5A5A5A', border: 'rgba(90,90,90,0.14)' },
                    { bg: 'rgba(55,65,81,0.06)', color: '#4B5563', border: 'rgba(55,65,81,0.1)' },
                  ];
                  const rankStyle = rankStyles[i] ?? { bg: 'rgba(0,0,0,0.04)', color: 'rgba(13,13,13,0.36)', border: 'rgba(0,0,0,0.07)' };
                  return (
                    <div
                      key={paddle?.id}
                      className="grid grid-cols-12 px-5 py-4 items-center"
                      style={{
                        borderBottom: '1px solid rgba(0,0,0,0.04)',
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.012)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div className="col-span-1">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black"
                          style={{ background: rankStyle.bg, color: rankStyle.color, border: `1px solid ${rankStyle.border}` }}
                        >
                          {i + 1}
                        </div>
                      </div>
                      <Link href={`/paddles/${paddle?.slug}`} className="col-span-4 flex items-center gap-3" style={{ textDecoration: 'none' }}>
                        <div
                          className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0"
                          style={{ background: 'linear-gradient(145deg, #F8F8F8, #EFEFEF)', border: '1px solid rgba(0,0,0,0.055)' }}
                        >
                          <img
                            src={paddle?.image}
                            alt={paddle?.imageAlt}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
                          />
                        </div>
                        <div>
                          <div className="text-sm font-semibold" style={{ color: '#0D0D0D', letterSpacing: '-0.015em', lineHeight: 1.3 }}>{paddle?.name}</div>
                          <div className="text-xs mt-0.5" style={{ color: 'rgba(13,13,13,0.34)' }}>{paddle?.brand}</div>
                        </div>
                      </Link>
                      <div className="col-span-2 text-center">
                        <span className="text-sm font-black tabular-nums" style={{ color: '#D90012' }}>{paddle?.score}</span>
                      </div>
                      <div className="col-span-2 text-center">
                        <span className="text-sm font-semibold tabular-nums" style={{ color: '#0D0D0D' }}>{paddle?.priceFormatted}</span>
                      </div>
                      <div className="col-span-2 text-center">
                        <span className="badge-neutral truncate block text-center">
                          {paddle?.firepowerTier || paddle?.paddleType || '—'}
                        </span>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <button
                          onClick={() => selected ? removeItem(paddle?.id) : addItem(paddle)}
                          className="w-7 h-7 rounded-full text-xs font-black flex items-center justify-center"
                          style={{
                            background: selected ? '#D90012' : 'rgba(0,0,0,0.04)',
                            color: selected ? '#fff' : 'rgba(13,13,13,0.36)',
                            border: selected ? 'none' : '1.5px solid rgba(0,0,0,0.08)',
                            transition: 'background var(--transition), color var(--transition), border-color var(--transition)',
                          }}
                          onMouseEnter={e => {
                            if (!selected) {
                              (e.currentTarget as HTMLElement).style.background = 'rgba(217,0,18,0.07)';
                              (e.currentTarget as HTMLElement).style.color = '#D90012';
                              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(217,0,18,0.2)';
                            }
                          }}
                          onMouseLeave={e => {
                            if (!selected) {
                              (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0.04)';
                              (e.currentTarget as HTMLElement).style.color = 'rgba(13,13,13,0.36)';
                              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,0,0,0.08)';
                            }
                          }}
                          title={selected ? 'Remove from compare' : 'Add to compare'}
                        >
                          {selected ? '✓' : '+'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
